// aulas.<domínio>/tv — autoriza a TV a entrar com a sua conta, pelo código
// que aparece na tela da TV.

import { getRouteApi } from "@tanstack/react-router";
import { CheckCircle2, Tv } from "lucide-react";
import { useState, type FormEvent } from "react";
import { ErrorBox, PageHeader } from "../components/ui";
import { api, useSession } from "../lib/api";

const route = getRouteApi("/_app/tv");

type Step = { kind: "code" } | { kind: "confirm"; code: string; device: string } | { kind: "done"; approved: boolean };

export function TvPage() {
  const { codigo } = route.useSearch();
  const session = useSession()!;
  const [code, setCode] = useState(format(codigo ?? ""));
  const [step, setStep] = useState<Step>({ kind: "code" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const lookup = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const r = await api.unwrap(api.client.GET("/v1/auth/device/{code}", { params: { path: { code } } }));
      setStep({ kind: "confirm", code, device: r.device_name });
    });
  };

  const decide = (approve: boolean) =>
    run(async () => {
      if (step.kind !== "confirm") return;
      await api.unwrap(
        api.client.POST("/v1/auth/device/{code}/approve", { params: { path: { code: step.code } }, body: { approve } }),
      );
      setStep({ kind: "done", approved: approve });
    });

  return (
    <div className="mx-auto max-w-md">
      <PageHeader title="Entrar na TV" subtitle="Digite o código que aparece na tela da TV." />
      <div className="card space-y-5 p-6">
        {step.kind === "code" && (
          <form onSubmit={lookup} className="space-y-4">
            <input
              aria-label="Código da TV"
              className="field text-center font-mono text-3xl tracking-[0.25em] uppercase"
              placeholder="XXXX-XXXX"
              autoFocus
              autoComplete="off"
              maxLength={9}
              value={code}
              onChange={(e) => setCode(format(e.target.value))}
            />
            {error ? <ErrorBox error={error} /> : null}
            <button className="btn-primary w-full" disabled={busy || code.length !== 9}>
              Continuar
            </button>
          </form>
        )}

        {step.kind === "confirm" && (
          <div className="space-y-4 text-center">
            <Tv className="mx-auto h-10 w-10 text-accent" aria-hidden />
            <p>
              <strong>{step.device}</strong> quer entrar com a conta de{" "}
              <strong>{session.user.display_name || session.user.email}</strong>.
            </p>
            <p className="text-xs text-muted">
              Só autorize se essa TV estiver com você agora. Dá para desconectar depois em Console → Usuários.
            </p>
            {error ? <ErrorBox error={error} /> : null}
            <div className="flex gap-2">
              <button className="btn-ghost flex-1" disabled={busy} onClick={() => void decide(false)}>
                Recusar
              </button>
              <button className="btn-primary flex-1" disabled={busy} onClick={() => void decide(true)}>
                Autorizar
              </button>
            </div>
          </div>
        )}

        {step.kind === "done" && (
          <div className="space-y-2 text-center">
            <CheckCircle2 className={`mx-auto h-10 w-10 ${step.approved ? "text-ok" : "text-muted"}`} aria-hidden />
            <p className="font-semibold">{step.approved ? "Pronto! A TV já está entrando." : "Acesso recusado."}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/** Deixa o código como "ABCD-1234" enquanto a pessoa digita. */
export function format(v: string): string {
  const c = v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
  return c.length > 4 ? `${c.slice(0, 4)}-${c.slice(4)}` : c;
}
